import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { ClubMembershipView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import {
  canSubmitJoin,
  hasOpenMembership,
  joinRequestBody,
  membershipsPath,
} from './clubMembership';

export interface ClubMembershipSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
  athleteId: string;
  /** Whether the person may start and end memberships; the server enforces it as well. */
  canManage: boolean;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

/** The club membership periods of one athlete, kept apart from team and group placements. */
export function ClubMembershipSection({
  locale,
  base,
  csrfToken,
  athleteId,
  canManage,
}: ClubMembershipSectionProps): React.JSX.Element {
  const [memberships, setMemberships] = useState<ClubMembershipView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [joinedOn, setJoinedOn] = useState('');
  const [confirmingEnd, setConfirmingEnd] = useState<string | null>(null);
  // A guardian whose link does not allow the membership view gets not-found; show nothing then.
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      setMemberships(await apiRequest<ClubMembershipView[]>(membershipsPath(base, athleteId)));
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 404 && !canManage) setHidden(true);
      else setErrorKey(errorKeyOf(error));
    }
  }, [base, athleteId, canManage]);

  useEffect(() => {
    void load();
  }, [load]);

  async function join(): Promise<void> {
    setErrorKey(null);
    try {
      await apiRequest<ClubMembershipView>(membershipsPath(base, athleteId), {
        method: 'POST',
        body: joinRequestBody(joinedOn),
        csrfToken,
      });
      setJoinedOn('');
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  async function end(membership: ClubMembershipView): Promise<void> {
    setErrorKey(null);
    setConfirmingEnd(null);
    try {
      await apiRequest<ClubMembershipView>(membershipsPath(base, athleteId, membership.id), {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: membership.version,
      });
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      await load();
    }
  }

  if (hidden) return <></>;

  return (
    <Box component="section" aria-label={translate(locale, 'clubMembership.title')} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" component="h4" gutterBottom>
        {translate(locale, 'clubMembership.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {memberships === null && errorKey === null && (
        <Typography>{translate(locale, 'clubMembership.loading')}</Typography>
      )}
      {memberships?.length === 0 && (
        <Typography color="text.secondary">{translate(locale, 'clubMembership.empty')}</Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {memberships?.map((membership) => (
          <Box
            component="li"
            key={membership.id}
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <Typography variant="body2">
              {translate(locale, 'clubMembership.from')}{' '}
              {formatCalendarDate(membership.joinedOn, locale)}
              {' · '}
              {translate(locale, 'clubMembership.until')}{' '}
              {membership.leftOn === null
                ? translate(locale, 'clubMembership.openEnded')
                : formatCalendarDate(membership.leftOn, locale)}
            </Typography>
            {canManage && membership.leftOn === null && (
              <Button
                size="small"
                color={confirmingEnd === membership.id ? 'error' : 'primary'}
                variant={confirmingEnd === membership.id ? 'contained' : 'outlined'}
                onClick={() => {
                  if (confirmingEnd === membership.id) void end(membership);
                  else setConfirmingEnd(membership.id);
                }}
              >
                {translate(
                  locale,
                  confirmingEnd === membership.id
                    ? 'clubMembership.end.confirm'
                    : 'clubMembership.end',
                )}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
      {canManage && memberships !== null && !hasOpenMembership(memberships) && (
        <Box
          component="form"
          sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
          onSubmit={(event) => {
            event.preventDefault();
            void join();
          }}
        >
          <TextField
            size="small"
            type="date"
            label={translate(locale, 'clubMembership.joinedOn')}
            value={joinedOn}
            onChange={(event) => {
              setJoinedOn(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={!canSubmitJoin(joinedOn)}
            sx={{ mt: 0.5 }}
          >
            {translate(locale, 'clubMembership.join')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
